import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState, MouseEvent, useMemo, CSSProperties } from 'react'
import './index.scss'
import { ICON } from '../../../components/Icon/data'
import Icon from '../../../components/Icon'
import { CARD_KEYS, CARD_MAP } from '../data'
import { COLOR, FORMAT_TYPE } from '../../../data/variables'
import { DTO_Dashboard } from '../../../services/calls/types/dashboard'
import useDynamicRef from '../../../hooks/useDynamicRef'
import { useFormat } from '../../../hooks/useFormat'
import { useTranslation } from 'react-i18next'
import { isValue } from '../../../services/utils/misc'

export type Position = {
  x: number
  y: number
}

type Card = {
  type: string
  value?: number | null
  isDraggable?: boolean
  isHolder?: boolean
  data: DTO_Dashboard
  secondaryValue?: number | string
  makeSpace?: (pos: Position) => void
  onDrag?: (type: string | null) => void
  isRemovable?: boolean
  onRemove?: () => void
  isLoading?: boolean
  onClick?: () => void
}

const initial_position: Position = { x: 0, y: 0 }
const default_middle: Position = { x: 168, y: 143 }

const Card = memo(forwardRef(({
  type, value, isDraggable = false, isHolder = false, data, secondaryValue, makeSpace = () => {},
  onDrag = () => {}, isRemovable = false, onRemove, isLoading = true, onClick
}: Card, ref) => {
  const { t } = useTranslation()
  const positionRef = useRef()

  const [position, setPosition] = useState<Position>(initial_position)
  const [isDragging, setIsDragging] = useState<boolean>(false)

  const { gradeToLetter } = useFormat()

  const { icon, unit, text, showArrow } = CARD_MAP?.[type] ?? {}

  const handleMouseDown = useCallback((e: MouseEvent) => {
    setIsDragging(true)
    setPosition({
      x: e.clientX - default_middle.x,
      y: e.clientY - default_middle.y,
    })
    onDrag(type)
    e.preventDefault()
  }, [position, onRemove])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return

    const newPosition = {
      x: e.clientX - default_middle.x,
      y: e.clientY - default_middle.y,
    }

    makeSpace(newPosition)
    setPosition(newPosition)
  }, [isDragging, makeSpace])

  const handleMouseUp = () => {
    setIsDragging(false)
    setPosition(initial_position)
  }

  const getPosition = useCallback((): DOMRect => positionRef.current.getBoundingClientRect(), [positionRef.current])

  const forwardRefAction = useDynamicRef({
    getPosition
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  useEffect(() => {
    if (isDraggable || isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
    } else {
      window.removeEventListener('mousemove', handleMouseMove)
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [isDraggable, isDragging])

  const formatFunction = useCallback((val: number | null, isSecondary: boolean = false) => {
    const { format } = CARD_MAP?.[type] ?? {}
    if (val === null || val === undefined) {
      return '-'
    } else if (type === CARD_KEYS.MAX_SPEED && isSecondary) {
      return `${t('Registration number')}: ${data?.interval_range?.max_speed_globalid ?? ''}`
    } else if (type === CARD_KEYS.MAX_SPEED_LIMIT && isSecondary) {
      return `${t('Registration number')}: ${data?.interval_range?.max_speed_over_limit_globalid ?? ''}`
    } else if (format === FORMAT_TYPE.NUMBER) {
      return val?.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    } else if (format === FORMAT_TYPE.ONE_DECIMAL) {
      return val?.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    } else if (format === FORMAT_TYPE.LETTER_GRADE) {
      return gradeToLetter(val)
    } else if (format === FORMAT_TYPE.DURATION) {
      const minutes = val / 60
      const hours = Math.floor(minutes / 60)
      const minutesLeft = minutes - (hours * 60)
      return minutesLeft > 30 ? hours + 1 : hours
    }
    return val
  }, [type, data, t])

  const postText = useMemo(() => {
    if (type === CARD_KEYS.MAX_SPEED || type === CARD_KEYS.MAX_SPEED_LIMIT) return ''
    if (unit) return `${unit} ${t(text)}`
    return t(text)
  }, [unit, text, t])

  const style = useMemo(() => {
    const styles: CSSProperties = {}
    if (position && isDragging) {
      styles.position = 'absolute'
      styles.left = position.x
      styles.top = position.y
    }
    return styles
  }, [position, isDragging])

  const benchmark = useMemo(() => {
    if (showArrow && isValue(value) && isValue(secondaryValue)) {
      if (typeof value === 'number' && typeof secondaryValue === 'number') return value > secondaryValue
      else if (typeof value === 'string' && typeof secondaryValue === 'string') return value > secondaryValue
    }

    return false
  }, [showArrow, value, secondaryValue])

  if (isLoading) {
    return (
      <div className="Card isLoading">
        <div className="Card__shimmer" />
      </div>
    )
  }

  return (
    <div
      ref={(el) => positionRef.current = el}
      className={`Card${isHolder && !isDragging ? ' holder' : ''}`}
      onMouseDown={(e) => {
        if (!isDraggable) return
        handleMouseDown(e)
      }}
      onMouseUp={() => handleMouseUp()}
      onClick={() => {
        if (!isDraggable && onClick) onClick()
      }}
      style={style}
    >
      {isRemovable && onRemove && (
        <div className="Card__button-wrapper">
          <div
            className="Card__button-remove"
            role="button"
            tabIndex={0}
            onMouseDown={(e) => { // Have to use onMouseDown to stop the drag event on click
              e.stopPropagation() // Stop parent mousedown action
              onRemove()
            }}
          >
            <Icon icon={ICON.CLOSE} />
          </div>
        </div>
      )}
      <div className="Card__header">
        <Icon icon={icon} color={isHolder ? COLOR.GRAY_30 : COLOR.TEXT_GRAY} />
        {`${formatFunction(value)}${unit ? ` ${t(unit)}` : ''}`}
        <div className="Card__header-arrow">
          {showArrow && <Icon icon={benchmark ? ICON.ARROW_UP : ICON.ARROW_DOWN} color={isHolder ? COLOR.GRAY_30 : COLOR.TEXT_GRAY} />}
        </div>
      </div>
      <div className="Card__text">{t(text)}</div>
      <div className="Card__extra">
        {`${formatFunction(secondaryValue, true)} ${postText}`}
      </div>
    </div>
  )
}))

export default Card
